import React, { useState, memo, useEffect } from 'react'
import { Button as VendorButton } from 'react-bootstrap'
import styled from 'styled-components'
import ChromePicker from 'react-color/lib/Chrome'

import { _ } from 'utils'
import { $gray_900 } from 'utils/colors'

import { EditorMode } from '../types'
import AssetCardContainer from './AssetCardContainer'

const S = {
    Swatch: styled.span`
        display: block;
        width: 100%;
        height: 100%;
        min-height: 162px;
        border-radius: 4px;
        border: 1px solid rgba(24, 28, 33, 0.15);
    `,
    AssetPreview: styled.div`
        min-height: 90px;
        height: 100%;
        position: relative;
        flex-grow: 1;
        flex-shrink: 1;
    `,
    Text: styled.p`
        color: ${$gray_900} !important;
        font-weight: 300;
        flex-grow: 0;
        flex-shrink: 0;
    `,
    Button: styled(VendorButton)`
        align-self: flex-end;
        flex-grow: 0;
        flex-shrink: 0;
    `,
    Nav: styled.div`
        display: flex;
        width: 100%;
        margin-top: auto;
        flex-direction: row;
        align-self: flex-end;
    `,
    NavButton: styled(VendorButton)`
        flex-grow: 1;
        flex-shrink: 1;
    `,
    ChromePicker: styled(ChromePicker)`
        box-shadow: unset !important;
    `,
}

export interface AssetColorCardProps {
    buttonText: string
    defaultValue: string
    onUpdate(value: string): void
    title: string
}

interface Color {
    hex: string
}

const AssetColorCard = ({
    buttonText,
    defaultValue,
    onUpdate,
    title,
}: AssetColorCardProps) => {
    const [value, setValue] = useState(defaultValue)
    const [editMode, setEditMode] = useState(EditorMode.preview)

    const buttonChangeClickHandler = () => {
        setEditMode(EditorMode.write)
    }

    const buttonUpdateClickHandler = () => {
        setEditMode(EditorMode.preview)
        onUpdate(value)
    }

    const buttonCancelClickHandler = () => {
        setEditMode(EditorMode.preview)
        setValue(defaultValue)
    }

    const colourChangeHandler = (color: Color) => {
        setValue(color.hex)
    }

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    return (
        <AssetCardContainer title={title}>
            {editMode === EditorMode.preview ? (
                <>
                    <S.AssetPreview className="w-100 text-center">
                        <S.Swatch
                            style={{
                                backgroundColor: value,
                            }}
                        />
                    </S.AssetPreview>
                    <S.Text className="w-100 text-center pt-3 mb-3">
                        {value}
                    </S.Text>
                </>
            ) : (
                <>
                    <S.ChromePicker
                        color={value}
                        onChange={colourChangeHandler}
                        width="100%"
                    />
                    <S.Nav>
                        <S.NavButton
                            variant="primary mt-2 mr-2"
                            onClick={buttonUpdateClickHandler}
                        >
                            {_('form.save')}
                        </S.NavButton>
                        <S.NavButton
                            variant="default mt-2"
                            onClick={buttonCancelClickHandler}
                        >
                            {_('form.cancel')}
                        </S.NavButton>
                    </S.Nav>
                </>
            )}
            {editMode === EditorMode.preview && (
                <S.Button
                    variant="primary"
                    className="w-100 mt-auto mb-0"
                    onClick={buttonChangeClickHandler}
                >
                    {buttonText}
                </S.Button>
            )}
        </AssetCardContainer>
    )
}

export default memo(AssetColorCard)
